import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid, MenuItem } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { OtaContext } from "../../../context";
import { FilledSelect } from "../../../components/FormElements";
import { useTestIds } from "../../../hooks/useTestIds";

const OtaSelectField = ({
  otaKey,
  multiSelect,
  menuOptions,
  exclude,
  isBoolean,
  markAsDefault,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const testIds = useTestIds("ota-select-field");

  const { otaFilterParams, setOtaFilterParams } = useContext(OtaContext);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const paramsKey = multiSelect ? `${otaKey}__in` : otaKey;
  const excludeParamsKey = multiSelect ? `${otaKey}__not__in` : `${otaKey}__not`;
  const getMapKey = (option) => (multiSelect ? option : option ?? "__clear__");
  const getMenuOption = (option) => (multiSelect ? option : option ?? "-- clear selection --");

  const negateBoolean = (bool) => {
    if (typeof bool === "undefined") return undefined;

    return bool === "true" ? "false" : "true";
  };

  useEffect(() => {
    if (isBoolean) onChange(otaFilterParams[paramsKey]);
    else if (exclude && otaFilterParams[paramsKey]) onChange(otaFilterParams[paramsKey]);
    else if (!exclude && otaFilterParams[excludeParamsKey])
      onChange(otaFilterParams[excludeParamsKey]);
  }, [exclude]);

  const onChange = (value) => {
    if (exclude) {
      if (isBoolean)
        setOtaFilterParams({
          [paramsKey]: negateBoolean(value),
        });
      else
        setOtaFilterParams({
          [excludeParamsKey]: value,
          [paramsKey]: undefined,
        });
    } else
      setOtaFilterParams({
        [paramsKey]: value,
        [excludeParamsKey]: undefined,
      });
  };

  const getDisplayValue = () => {
    if (exclude)
      if (isBoolean) return negateBoolean(otaFilterParams[paramsKey]);
      else return otaFilterParams[excludeParamsKey];

    return otaFilterParams[paramsKey] || "";
  };

  function Asterisk({ show }) {
    if (!show) return null;
    return (
      <span
        data-testid={testIds.asterisk}
        style={{
          position: "absolute",
          color: theme.palette.text.red,
          marginLeft: -theme.spacing(1.1),
        }}
      >
        *
      </span>
    );
  }

  Asterisk.propTypes = {
    show: PropTypes.bool,
  };

  Asterisk.defaultProps = {
    show: false,
  };

  return (
    <Grid item md={3} xs={12} data-testid={testIds.wrapper}>
      <FilledSelect
        label={t(`OTA.Filters.${otaKey}`)}
        value={getDisplayValue()}
        SelectProps={{
          "data-testid": testIds.select,
          multiple: multiSelect,
          style: { background: theme.palette.background.paper },
          onOpen: () => setMenuIsOpen(true),
          onClose: () => setMenuIsOpen(false),
        }}
        FormControlProps={{
          fullWidth: true,
          margin: "normal",
          variant: "outlined",
          size: "small",
        }}
        onChange={(e) => onChange(e.target.value)}
      >
        {(isBoolean ? [undefined, "true", "false"] : menuOptions)?.map((option, idx) => {
          const key = getMapKey(option);
          const value = getMenuOption(option);
          return (
            <MenuItem key={key} value={option} data-testid={`${testIds.option}-${idx}`}>
              {<Asterisk show={menuIsOpen && markAsDefault && markAsDefault === value} />}
              {value}
            </MenuItem>
          );
        })}
      </FilledSelect>
    </Grid>
  );
};

OtaSelectField.propTypes = {
  isBoolean: PropTypes.bool,
  exclude: PropTypes.bool,
  otaKey: PropTypes.string.isRequired,
  multiSelect: PropTypes.bool,
  menuOptions: PropTypes.array,
  markAsDefault: PropTypes.string,
};

OtaSelectField.defaultProps = {
  isBoolean: false,
  exclude: false,
  multiSelect: false,
  menuOptions: [],
  markAsDefault: null,
};

export default OtaSelectField;
